import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getPendingRecord } from "@/offline/db";

/** One photo of a queued multi-photo draft, with a ready-to-render blob URL. */
export interface PendingRecordPhoto {
  /** `client_photo_uuid` — the grid key + viewer-index identity. */
  photoId: string;
  /** Object URL minted from the photo's locally captured annotated blob. */
  url: string;
}

/**
 * Resolves the per-photo previews of a QUEUED MULTI-photo draft for the modal's
 * grid + fullscreen carousel, and tracks the outbox query so appended photos
 * appear live while the viewer is open.
 *
 * A multi-photo create is ONE outbox `PendingRecord` carrying a `photos[]`
 * array (see [[offline-record-outbox]]). The cover (`photos[0]`) already
 * renders everywhere via the entry's mirrored top-level blob (`pendingTileUrl`
 * / `effectiveSrc`), but the additional photos live only inside the outbox
 * entry — there is no server `images[]` and no offline asset cache for a draft.
 * So when the modal opens on such a draft this hook reads the entry (by its
 * `clientUuid`, which is the record id) and mints an object URL per photo's
 * annotated blob, so the modal can render the whole group from local bytes —
 * online or offline, no server call.
 *
 * Reactivity: the hook is keyed under `["pendingRecords", "photos", recordId]`
 * with `staleTime: Infinity` so it only refetches when explicitly invalidated.
 * `appendPhotosToPendingRecord` (Task 2.1) bumps the outbox entry's `revision`
 * and the caller (Task 2.3) calls `invalidateQueries({ queryKey: ["pendingRecords"] })`
 * — the prefix invalidation refetches this key, which then flows through the
 * URL-cache effect and yields the updated tile list without remounting the viewer.
 *
 * Object-URL lifecycle (mirrors `usePendingAppends`): URLs are cached per
 * `photoId` in a state-backed `Map`. An effect reconciles against the latest
 * query result — minting a URL for any newly-seen `photoId`, and revoking a
 * URL when its photo leaves the entry, when the hook is DISABLED (e.g. the
 * draft synced while its viewer was still open — the photos source collapses
 * to empty so every cached URL is revoked), and on unmount. A `photoId`'s blob
 * is immutable once queued, so `photoId` alone is the cache key and no URL is
 * ever re-minted for the same photo.
 *
 * A single-photo draft (or a synced record, or a closed modal) yields `[]`,
 * so the caller's existing single-cover path is untouched.
 *
 * @param recordId The open record's id (= the draft's `clientUuid`), or null.
 * @param enabled  True only while the modal is open on a pending record — the
 *                 caller gates this so a synced record never triggers an IDB
 *                 read.
 */
export function usePendingRecordPhotos(
  recordId: string | null | undefined,
  enabled: boolean,
): PendingRecordPhoto[] {
  const query = useQuery({
    queryKey: ["pendingRecords", "photos", recordId ?? "none"] as const,
    queryFn: () => (recordId ? getPendingRecord(recordId) : undefined),
    enabled: enabled && !!recordId,
    // The pendingRecords store is only mutated by explicit outbox operations
    // (put, append, drain). Refetch is triggered by prefix invalidation from
    // those callers, not by staleness.
    staleTime: Infinity,
    gcTime: 0,
  });

  // Gate the photos source on `enabled` too: when the hook is disabled (e.g.
  // the draft synced while its viewer was still open), TanStack RETAINS
  // `query.data`, so reading it directly would keep returning stale tiles AND
  // keep their object URLs minted. Collapsing to `undefined` here makes the
  // reconcile effect below treat it as "all photos vanished" and revoke them.
  const photos = enabled ? query.data?.photos : undefined;

  // Object-URL cache keyed by `photoId`, held in STATE (not a ref). All
  // minting and revoking happens in the effect below — never during render —
  // so render is a pure read of this map. A URL is minted once per photoId
  // and reused across re-renders; it is revoked when its photo leaves the entry.
  const [urlCache, setUrlCache] = useState<ReadonlyMap<string, string>>(
    () => new Map(),
  );

  // Reconcile the URL cache against the current photos: mint a URL for any
  // newly-seen photoId, revoke any that has gone stale. This is the ONLY place
  // object URLs are created or revoked — no side effects during render.
  //
  // It does NOT early-return on empty/disabled `photos`: that case is treated
  // as "all photos vanished", so an empty list (a disabled hook, or a record
  // that emptied) revokes every cached URL — restoring the revoke-on-disable
  // contract. The updater is a no-op (`return prev`) whenever nothing changed,
  // so it cannot loop — hence the scoped `set-state-in-effect` disable.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- object-URL lifecycle sync with async query data; guarded no-op when unchanged
    setUrlCache((prev) => {
      // Fast path: nothing cached AND nothing to mint — no work, no churn.
      if (prev.size === 0 && (!photos || photos.length === 0)) return prev;
      const liveKeys = new Set((photos ?? []).map((p) => p.photoId));
      let changed = false;
      const next = new Map(prev);
      // Revoke + drop any URL whose photo left the entry (or all of them when
      // `photos` is empty/disabled).
      for (const [key, url] of prev) {
        if (!liveKeys.has(key)) {
          URL.revokeObjectURL(url);
          next.delete(key);
          changed = true;
        }
      }
      // Mint a URL for any photo not yet in the cache.
      for (const photo of photos ?? []) {
        if (!next.has(photo.photoId)) {
          next.set(photo.photoId, URL.createObjectURL(photo.annotatedBlob));
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [photos]);

  // Mirror the latest URL cache into a ref so the unmount cleanup below can
  // revoke every URL WITHOUT calling a state setter on an unmounting hook.
  const urlCacheRef = useRef(urlCache);
  useEffect(() => {
    urlCacheRef.current = urlCache;
  }, [urlCache]);

  // Revoke every outstanding URL when the hook unmounts — no leaks.
  useEffect(() => {
    return () => {
      for (const url of urlCacheRef.current.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  // Pure derivation: build the tiles from `photos` and the state-backed URL
  // cache. A photo whose URL has not been minted yet (the effect runs after
  // this render) is skipped — it appears on the very next render once the
  // cache state updates. No ref access, no side effects.
  return useMemo<PendingRecordPhoto[]>(() => {
    if (!photos || photos.length === 0) return [];
    const tiles: PendingRecordPhoto[] = [];
    for (const photo of photos) {
      const url = urlCache.get(photo.photoId);
      if (!url) continue;
      tiles.push({ photoId: photo.photoId, url });
    }
    return tiles;
  }, [photos, urlCache]);
}
